import React, { useState, useRef } from 'react';
import { FiUploadCloud, FiFile, FiX, FiCheck, FiLoader, FiEye, FiRotateCw } from 'react-icons/fi';
import API from '../api/axios';
import toast from 'react-hot-toast';
import './FileUpload.css';

const MAX_FILES = 15;
const MAX_SIZE_MB = 50;
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.doc', '.docx', '.ppt', '.pptx'];

const FileUpload = ({ onFilesUploaded }) => {
  const [files, setFiles] = useState([]); // Array of { id, file, status, progress, error }
  const [uploadedFiles, setUploadedFiles] = useState([]); // Completed uploaded files
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const fileInputRef = useRef(null);

  const isPDF = (name) => name.toLowerCase().endsWith('.pdf');

  const getFileIcon = (name) => {
    const ext = name.split('.').pop().toLowerCase();
    const icons = { pdf: '📄', jpg: '🖼️', jpeg: '🖼️', png: '🖼️', webp: '🖼️', heic: '🖼️', heif: '🖼️', doc: '📝', docx: '📝', ppt: '📽️', pptx: '📽️' };
    return icons[ext] || '📁';
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const calculateTimeout = (file) => {
    const sizeInBytes = file.size;
    let speedBytesPerSec = 100 * 1024; // Baseline default upload speed: 100 KB/s
    
    // Attempt to read current network speed (if connection API is available)
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn && conn.downlink) {
      // estimated upload speed is ~30% of downlink speed in general
      const estimatedUploadSpeedMbps = conn.downlink * 0.3; 
      if (estimatedUploadSpeedMbps > 0) {
        speedBytesPerSec = (estimatedUploadSpeedMbps * 1024 * 1024) / 8;
      }
    }
    
    // Transfer duration
    const transferTimeMs = (sizeInBytes / speedBytesPerSec) * 1000;
    
    // Add conversion latency safety margin for DOCX/PPTX
    const ext = file.name.split('.').pop().toLowerCase();
    const needsConversion = ['doc', 'docx', 'ppt', 'pptx'].includes(ext);
    const conversionBufferMs = needsConversion ? 20000 : 0; 
    
    const paddingMs = 15000; // General network delay pad
    const calculated = transferTimeMs + conversionBufferMs + paddingMs;
    
    // Enforce limits: Min 30 seconds, Max 5 minutes
    return Math.round(Math.max(30000, Math.min(300000, calculated)));
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    const totalCount = files.length + uploadedFiles.length + selected.length;

    if (totalCount > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    const valid = [];
    for (const file of selected) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        toast.error(`${file.name}: Unsupported file type`);
        continue;
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.error(`${file.name}: Exceeds ${MAX_SIZE_MB}MB limit`);
        continue;
      }
      valid.push({
        id: Date.now() + '_' + Math.random().toString(36).substring(2, 9),
        file,
        status: 'pending',
        progress: 0,
        error: null,
        stage: null
      });
    }

    setFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const removeUploaded = (index) => {
    setUploadedFiles(prev => {
      const updated = prev.filter((_, i) => i !== index);
      onFilesUploaded?.(updated);
      return updated;
    });
  };

  const uploadSingleFile = async (fileObj) => {
    // Set state to uploading
    setFiles(prev => prev.map(f => f.id === fileObj.id ? { ...f, status: 'uploading', progress: 0, error: null, stage: 'Starting transfer...' } : f));
    
    const formData = new FormData();
    formData.append('files', fileObj.file);
    
    const fileTimeout = calculateTimeout(fileObj.file);
    
    let processingInterval = null;
    let currentProgress = 0;
    
    const ext = fileObj.file.name.split('.').pop().toLowerCase();
    const isDoc = ['doc', 'docx', 'ppt', 'pptx'].includes(ext);
    
    try {
      const { data } = await API.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: fileTimeout,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          
          if (percentCompleted < 100) {
            // Map Axios network progress to 0-75% in the progress bar
            currentProgress = Math.round(percentCompleted * 0.75);
            setFiles(prev => prev.map(f => f.id === fileObj.id ? { 
              ...f, 
              progress: currentProgress, 
              stage: `Uploading (${currentProgress}%)` 
            } : f));
          } else {
            // Network upload is 100% complete, transitioning to active server processing phase (75-95%)
            if (!processingInterval) {
              currentProgress = 75;
              const stageMsg = isDoc ? 'Converting to PDF on server...' : 'Optimizing file on server...';
              
              setFiles(prev => prev.map(f => f.id === fileObj.id ? { 
                ...f, 
                progress: currentProgress, 
                stage: stageMsg 
              } : f));
              
              // Increment slowly to simulate active background server processing
              processingInterval = setInterval(() => {
                currentProgress = Math.min(95, currentProgress + Math.floor(Math.random() * 3) + 1);
                setFiles(prev => prev.map(f => f.id === fileObj.id ? { 
                  ...f, 
                  progress: currentProgress,
                  stage: currentProgress >= 90 ? 'Finishing upload...' : stageMsg
                } : f));
              }, 600);
            }
          }
        }
      });
      
      if (processingInterval) clearInterval(processingInterval);
      
      const uploadedInfo = data.files?.[0];
      if (!uploadedInfo) throw new Error('Upload succeeded but returned no details.');
      
      // Remove from pending list (handleUpload will add to uploadedFiles)
      setFiles(prev => prev.filter(f => f.id !== fileObj.id));
      return { 
        success: true, 
        file: { 
          ...uploadedInfo, 
          file: fileObj.file, 
          localFile: fileObj.file 
        } 
      };
    } catch (err) {
      if (processingInterval) clearInterval(processingInterval);
      
      console.error(`Upload failed for ${fileObj.file.name}:`, err);
      let errMsg = err.response?.data?.error || err.message || 'Upload failed';
      if (err.code === 'ECONNABORTED' || err.message?.toLowerCase().includes('timeout')) {
        errMsg = `Timeout (network slow). Estimated timeout was ${(fileTimeout / 1000).toFixed(0)}s.`;
      }
      
      setFiles(prev => prev.map(f => f.id === fileObj.id ? { ...f, status: 'failed', error: errMsg, progress: 0, stage: null } : f));
      return { success: false, error: errMsg };
    }
  };

  const handleUpload = async () => {
    const toUpload = files.filter(f => f.status === 'pending' || f.status === 'failed');
    if (toUpload.length === 0) return;

    setUploading(true);
    
    // Upload files concurrently
    const results = await Promise.all(toUpload.map(f => uploadSingleFile(f)));
    const successes = results.filter(r => r.success);
    const failures = results.filter(r => !r.success);

    if (successes.length > 0 && failures.length === 0) {
      toast.success('All files uploaded successfully! 🎉');
    } else if (successes.length > 0 && failures.length > 0) {
      toast.success(`${successes.length} file(s) uploaded, ${failures.length} failed.`);
    } else if (failures.length > 0) {
      toast.error('Upload failed. Please check and retry.');
    }
    
    setUploading(false);

    if (successes.length > 0) {
      const successfulFiles = successes.map(r => r.file);
      setUploadedFiles(prev => {
        const next = [...prev, ...successfulFiles];
        onFilesUploaded?.(next);
        return next;
      });
    }
  };

  const handleRetrySingle = async (fileObj) => {
    setUploading(true);
    const res = await uploadSingleFile(fileObj);
    if (res.success) {
      toast.success(`"${fileObj.file.name}" uploaded successfully!`);
      setUploadedFiles(prev => {
        const next = [...prev, res.file];
        onFilesUploaded?.(next);
        return next;
      });
    } else {
      toast.error(`"${fileObj.file.name}" upload failed: ${res.error}`);
    }
    setUploading(false);
  };

  return (
    <div className="file-upload">
      {/* Drop zone */}
      <div className="fu-dropzone" onClick={() => fileInputRef.current?.click()}>
        <FiUploadCloud className="fu-dropzone-icon" />
        <p className="fu-dropzone-title">Upload your files</p>
        <p className="fu-dropzone-desc">PDF, JPG, PNG, WEBP, DOC, DOCX, PPT, PPTX</p>
        <p className="fu-dropzone-limits">Max {MAX_SIZE_MB}MB per file • Max {MAX_FILES} files</p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>

      {/* Uploaded files */}
      {uploadedFiles.length > 0 && (
        <div className="fu-file-list">
          <div className="fu-list-header">
            <span>✅ Uploaded ({uploadedFiles.length})</span>
          </div>
          {uploadedFiles.map((f, i) => (
            <div className="fu-file-item uploaded" key={`up-${i}`}>
              <span className="fu-file-icon">{getFileIcon(f.name)}</span>
              <div className="fu-file-info">
                <span className="fu-file-name">{f.name}</span>
                <span className="fu-file-size">
                  <FiCheck size={12} color="var(--accent)" /> Uploaded
                </span>
              </div>
              <div className="fu-actions">
                {(isPDF(f.name) || 
                  f.name?.toLowerCase().endsWith('.doc') || 
                  f.name?.toLowerCase().endsWith('.docx') || 
                  f.name?.toLowerCase().endsWith('.ppt') || 
                  f.name?.toLowerCase().endsWith('.pptx')) && (
                  <button className="fu-preview" onClick={() => setPreviewFile(f)}>
                    <FiEye size={14} /> Preview
                  </button>
                )}
                <button className="fu-remove" onClick={() => removeUploaded(i)}>
                  <FiX size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pending / In-progress files */}
      {files.length > 0 && (
        <div className="fu-file-list">
          <div className="fu-list-header">
            <span>Selected ({files.length})</span>
          </div>
          {files.map((fileObj) => (
            <div className={`fu-file-item ${fileObj.status}`} key={fileObj.id}>
              <span className="fu-file-icon">{getFileIcon(fileObj.file.name)}</span>
              <div className="fu-file-info">
                <span className="fu-file-name">{fileObj.file.name}</span>
                <span className="fu-file-size">
                  {formatSize(fileObj.file.size)}
                  {fileObj.status === 'uploading' && ` • ${fileObj.stage || 'Uploading...'}`}
                  {fileObj.status === 'failed' && ` • Error: ${fileObj.error}`}
                </span>
                {fileObj.status === 'uploading' && (
                  <div className="fu-progress-bar-container">
                    <div className="fu-progress-bar" style={{ width: `${fileObj.progress}%` }}></div>
                  </div>
                )}
              </div>
              <div className="fu-actions">
                {fileObj.status === 'failed' && (
                  <button className="fu-retry-btn" onClick={() => handleRetrySingle(fileObj)} disabled={uploading}>
                    <FiRotateCw style={{ marginRight: '4px' }} className="fu-spin-hover" /> Retry
                  </button>
                )}
                <button className="fu-remove" onClick={() => removeFile(fileObj.id)} disabled={fileObj.status === 'uploading'}>
                  <FiX size={14} />
                </button>
              </div>
            </div>
          ))}

          <button
            className="fu-upload-btn"
            onClick={handleUpload}
            disabled={uploading || files.every(f => f.status === 'uploading')}
          >
            {uploading ? (
              <><FiLoader className="fu-spin" /> Uploading...</>
            ) : (
              <><FiUploadCloud /> Upload {files.filter(f => f.status === 'pending' || f.status === 'failed').length} file(s)</>
            )}
          </button>
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewFile && (
        <div className="fu-modal-overlay" onClick={() => setPreviewFile(null)}>
          <div className="fu-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fu-preview-header">
              <h4>{previewFile.name}</h4>
              <button className="fu-close" onClick={() => setPreviewFile(null)}>
                <FiX size={20} />
              </button>
            </div>
            <div className="fu-preview-content" style={{ height: '550px' }}>
              <iframe
                src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(previewFile.viewLink)}`}
                width="100%"
                height="100%"
                title="File Preview"
                style={{ border: 'none' }}
                sandbox="allow-scripts allow-same-origin"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
